import React, { useEffect, useState } from 'react';
import { useLive } from '../live.jsx';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/index.jsx';
import Marquee from './Marquee.jsx';
import { useFxBase, baseOptions, crossRates } from '../fxBase.js';
import { publicGet, metalName, formatMetalPrice, unitLabel, formatChange, formatUpdated, formatRate, formatDay } from '../metalPrices.js';

// Stock-exchange style band under the header (home, marketplace, /prices):
// one strip per enabled ticker (metals, exchange rates, marketplace
// activity), in the admin's order, each with its own label and accent.
// Scrolls continuously, pauses on hover or keyboard focus; with "reduce
// motion" each strip is a static row you can scroll sideways. Data comes
// from the public /api/tickers (our database only), reloaded every
// 5 minutes; strips with nothing to show aren't sent.

function useTickers() {
  const [strips, setStrips] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => publicGet('/tickers').then((d) => { if (alive) setStrips(d.strips || []); }).catch(() => {});
    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    window.addEventListener('pme:tickers', load);
    return () => { alive = false; clearInterval(timer); window.removeEventListener('pme:tickers', load); };
  }, []);
  // Changed in Admin › Tickers / Prices: read again at once.
  useLive(['tickers', 'prices'], () => window.dispatchEvent(new Event('pme:tickers')));
  return strips;
}

function Change({ item }) {
  const { lang } = useI18n();
  const ch = formatChange(item, lang);
  if (!ch) return null;
  return <span className={`ticker-change is-${ch.dir}`}><span aria-hidden="true">{ch.arrow}</span> {ch.text}</span>;
}

function MetalItem({ m }) {
  const { t, lang } = useI18n();
  return (
    <span className="ticker-item">
      <span className="ticker-name">{metalName(m, lang)}</span>
      <span className="ticker-price">{formatMetalPrice(m, lang)}<span className="ticker-unit">{unitLabel(m, t)}</span></span>
      <Change item={m} />
    </span>
  );
}

function FxItem({ r }) {
  const { lang } = useI18n();
  return (
    <span className="ticker-item">
      <span className="ticker-name">{r.code}/{r.base}</span>
      <span className="ticker-price">{formatRate(r.rate, lang)}</span>
      <Change item={r} />
    </span>
  );
}

function ActivityItem({ a, copy }) {
  const { t, lang } = useI18n();
  const qty = a.quantity !== null && a.quantity !== undefined
    ? `${new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { maximumFractionDigits: 2 }).format(a.quantity)}${a.unit ? ` ${a.unit}` : ''}`
    : a.moq !== null && a.moq !== undefined ? `${t('tickers.activity.moq')} ${new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { maximumFractionDigits: 2 }).format(a.moq)}${a.moqUnit ? ` ${t(`units.${a.moqUnit}`)}` : ''}` : '';
  const place = [a.city, a.province ? t(`provinces.${a.province}`) : ''].filter(Boolean).join(', ');
  return (
    <Link className="ticker-item ticker-link" to={a.url} tabIndex={copy ? -1 : undefined}>
      <span className="ticker-name">{t(`tickers.activity.${a.type}`)}</span>
      <span className="ticker-price">{a.title}</span>
      {[qty, place].filter(Boolean).map((x, i) => <span key={i} className="ticker-meta">· {x}</span>)}
    </Link>
  );
}

// Reading pace in pixels per second, so a preset feels the same whatever
// the number or length of items. 'custom' = the admin's seconds per loop.
const PX_PER_SECOND = { very_slow: 22, slow: 32, medium: 48, fast: 75 };
const DEFAULT_MOTION = { metals: { direction: 'left', speed: 'medium' }, fx: { direction: 'right', speed: 'slow' }, activity: { direction: 'left', speed: 'very_slow' } };
const DEFAULT_COLOR = { metals: 'metallic', fx: 'ocean', activity: 'brand' };

// Small icons in the label pills: metal bars, exchange arrows, storefront.
function TagIcon({ code }) {
  const common = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, className: 'tag-icon' };
  if (code === 'metals') {
    return <svg {...common}><path d="M3 20h8l-1.5-5h-5z" /><path d="M13 20h8l-1.5-5h-5z" /><path d="M8 13h8l-1.5-5h-5z" /></svg>;
  }
  if (code === 'fx') {
    return <svg {...common}><path d="M4 8h13" /><path d="M14 5l3 3-3 3" /><path d="M20 16H7" /><path d="M10 13l-3 3 3 3" /></svg>;
  }
  return <svg {...common}><path d="M4 10h16l-1.5-5h-13z" /><path d="M5 10v9h14v-9" /><path d="M10 19v-5h4v5" /></svg>;
}

// Base-currency menu at the start of the exchange-rate strip.
function FxBaseSelect({ items }) {
  const { t } = useI18n();
  const [base, setBase] = useFxBase();
  const options = baseOptions(items);
  return (
    <select className="ticker-base" value={options.includes(base) ? base : 'CAD'} onChange={(e) => setBase(e.target.value)} aria-label={t('tickers.fx.baseLabel')} title={t('tickers.fx.baseLabel')}>
      {options.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

function Strip({ strip, fxBase }) {
  const { t, lang } = useI18n();
  const motion = { ...DEFAULT_MOTION[strip.code], ...(strip.motion || {}) };
  const items = strip.code === 'fx' ? crossRates(strip.items, fxBase) : strip.items;
  const render = (copy) => items.map((it, i) => {
    if (strip.code === 'metals') return <MetalItem key={it.code} m={it} />;
    if (strip.code === 'fx') return <FxItem key={it.code} r={it} />;
    return <ActivityItem key={`${it.type}-${it.url}-${i}`} a={it} copy={copy} />;
  });
  const tagLink = strip.code === 'activity' ? '/marketplace' : '/prices';
  return (
    <section className={`ticker ticker-${strip.code} theme-${strip.color || DEFAULT_COLOR[strip.code]}`} aria-label={t(`tickers.${strip.code}.label`)}>
      <div className="ticker-tag">
        {strip.code === 'fx'
          ? <div className="tag-pill"><TagIcon code="fx" /><span className="tag-word">{t('tickers.fx.tagShort')}</span><FxBaseSelect items={strip.items} /></div>
          : <Link to={tagLink} className="tag-pill"><TagIcon code={strip.code} /><span className="tag-word">{t(`tickers.${strip.code}.tag`)}</span></Link>}
      </div>
      <Marquee render={render} direction={motion.direction} deps={[items, lang]}
        pxPerSecond={PX_PER_SECOND[motion.speed] || PX_PER_SECOND.medium}
        loopSeconds={motion.speed === 'custom' ? motion.loopSeconds : null} />
    </section>
  );
}

// Small information line under the strips (sources, times, disclaimers),
// scrolling slowly like the strips; wraps and stays still with
// "reduce motion".
function InfoLine({ metals, fx, fxBase }) {
  const { t, lang } = useI18n();
  const render = (copy) => [
    metals && <span key="m" className="note-item"><strong>{t('tickers.metals.noteLabel')}</strong> <Disclaimer data={metals} copy={copy} /></span>,
    fx && <span key="f" className="note-item"><strong>{t('tickers.fx.noteLabel')}</strong> {t('tickers.fx.disclaimer', { date: formatDay(fx.date, lang) })}{fxBase !== 'CAD' && ` ${t('tickers.fx.crossNote', { base: fxBase })}`}</span>,
    <span key="l" className="note-item"><Link to="/prices" tabIndex={copy ? -1 : undefined}>{t('prices.allPrices')} →</Link></span>,
  ].filter(Boolean);
  return (
    <div className="ticker-note">
      <Marquee render={render} direction="left" pxPerSecond={26} deps={[metals, fx, fxBase, lang]} label={t('tickers.noteLabel')} />
    </div>
  );
}

function Band() {
  const strips = useTickers();
  const [fxBase] = useFxBase();
  if (!strips || !strips.length) return null;
  const metals = strips.find((s) => s.code === 'metals');
  const fx = strips.find((s) => s.code === 'fx');
  const base = fx && baseOptions(fx.items).includes(fxBase) ? fxBase : 'CAD';
  return (
    <div className="ticker-wrap">
      {strips.map((s) => <Strip key={s.code} strip={s} fxBase={base} />)}
      {(metals || fx) && <InfoLine metals={metals} fx={fx} fxBase={base} />}
    </div>
  );
}

export function Disclaimer({ data, copy }) {
  const { t, lang } = useI18n();
  return (
    <span>
      {t('prices.disclaimer')} {t('prices.source')}{' '}
      <a href={data.source.url} target="_blank" rel="noreferrer noopener nofollow" tabIndex={copy ? -1 : undefined}>{data.source.name}</a>
      {data.updatedAt && <>, {t('prices.updated', { time: formatUpdated(data.updatedAt, lang) })}</>}.
    </span>
  );
}

// The band is decoration: if it ever fails to render, hide it rather than
// replacing the whole page with the error screen.
class TickerBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('[ticker]', err); }
  render() { return this.state.failed ? null : this.props.children; }
}

export default function PriceTicker() {
  return <TickerBoundary><Band /></TickerBoundary>;
}
